import { FunctionComponent } from 'preact';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'preact/hooks';
import { Icon } from '../../../../common/components/icon/icon';
import { COMMON_ICON_TYPES } from '../../../../common/components/icon/iconMapper';
import { getTimePresentation, isEmpty } from '../../../../common/utils/utils';
import {
  playAnotherVideoFromList,
  playVideoFromRecommendations,
  setUserInteraction,
} from '../../store/moreVideos.actions';
import { useStyles } from '../../../../common/hooks/useStyles';
import { getMiddleScreenVideoStyleObject } from './middleScreenVideo.style';
import { VideoData } from '../../../../common/store/store.types';
import { mediaDataSelector } from '../../../../common/store/selectors/mediaData.selector';
import { playerDataSelector } from '../../../../common/store/selectors/playerData.selector';
import { css } from '../../../../common/styles/styles.utils';
import { getSmallGalleryVideoUnitId } from '../../htmlElements/htmlElementsIdGetter';
import { VideoHeader } from './videoHeader/videoHeader';

const ARIA_LABEL = 'Play more videos';

interface MiddleScreenVideoVariablesProps {
  updatedVideoList: Array<VideoData>;
  currentVideoIndex: number;
  setExitGalleryView: (value: boolean) => void;
}

export const MiddleScreenVideo: FunctionComponent<MiddleScreenVideoVariablesProps> = props => {
  const { updatedVideoList, currentVideoIndex, setExitGalleryView } = props;

  const dispatch = useDispatch();
  const videoList = useSelector(mediaDataSelector.videoList);
  const videoTagStatus = useSelector(playerDataSelector.videoTagStatus);
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqueId);

  const [currentVideoTitle, setCurrentVideoTitle] = useState('');
  const [currentVideoThumbnail, setCurrentVideoThumbnail] = useState('');
  const [currentVideoDuration, setCurrentVideoDuration] = useState(0);

  const isSingleVideo = videoList.length === 1;
  const isEndState = isSingleVideo && videoTagStatus === 'complete';

  useEffect(() => {
    if (!isEmpty(updatedVideoList)) {
      const { duration, thumbnail, title } = updatedVideoList[currentVideoIndex];
      setCurrentVideoTitle(title);
      setCurrentVideoThumbnail(thumbnail);
      setCurrentVideoDuration(duration);
    }
  }, [currentVideoIndex, updatedVideoList]);

  const styles = useStyles(getMiddleScreenVideoStyleObject, [isEndState]);
  const { videoContainerStyle, videoStyle, thumbnailStyle, blurThumbnailStyle, playBlurIconStyle, durationStyle, titleStyle } = styles;

  const onVideoClicked = (): void => {
    const { index } = updatedVideoList[currentVideoIndex];
    dispatch(setUserInteraction('moreVideosUnitSelected'));
    if (isSingleVideo) {
      dispatch(playVideoFromRecommendations(index));
    } else {
      dispatch(playAnotherVideoFromList(index));
    }
  };

  const onVideoKeyDown = (event: any): void => {
    if (event.key === 'Enter') {
      onVideoClicked();
    }
  };

  return (
    <div style={css(videoContainerStyle)}>
      <VideoHeader setExitGalleryView={setExitGalleryView} isEndState={isEndState} />
      <div
        style={css(videoStyle)}
        onClick={onVideoClicked}
        id={getSmallGalleryVideoUnitId(playerUniqueId)}
        tabIndex={0}
        aria-label={ARIA_LABEL}
        onKeyDown={onVideoKeyDown}
      >
        <img src={currentVideoThumbnail} alt="video thumbnail" style={thumbnailStyle} />
        <div style={css(blurThumbnailStyle)} />
        <Icon
          icon={COMMON_ICON_TYPES.PLAY_BLUR}
          style={css(playBlurIconStyle)}
          focusable={false}
        />
        <div style={css(durationStyle)}>
          {getTimePresentation(Math.floor(currentVideoDuration))}
        </div>
      </div>
      <span style={css(titleStyle)}>
        {currentVideoTitle}
      </span>
    </div>
  );
};
